import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { NavLink } from 'react-router';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useForm } from 'react-hook-form';
import moment from 'moment/moment';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import Grid from '../../../components/base/Grid/Grid';
import { dateFormatter, getLegalEntityName, registryLinkFormatter } from '../../../utils/formatters';
import { APPLICATION_STATUS, REGISTRY_TYPE } from '../../../configs';
import { getRegisterData } from '../reducer';
import { getPaginationOpts, responseLogWithNotification } from '../../../utils/utils';
import { REGION_REGISTRY_MODULE } from '../../../configs/moduleNames';
import {
  loadRelationCarrierSzltList, loadRelationCarrierSzltListClear, loadRelationVehicleSzltList,
  loadRelationVehicleSzltListClear,
} from '../actions';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import Switches from '../../../components/fields/Switches/Switches';
import Pagination from '../../../components/base/Pagination/Pagination';
import RegistryPageModal from './RegistryPageModal';
import getDefaultSort from '../../../utils/getDefaultSort';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';

export default function SZLTMiniGrid(props) {
  const { setIsHistoryPopUp = () => {}, isCarrierRelationType = true, params = {} } = props;
  const dispatch = useDispatch();
  const [isRegistryModal, setIsRegistryModal] = useState(false);
  const {
    relationCarrierSzltList = [],
    relationCarrierSzltListMeta = {},
    relationVehicleSzltList = [],
    relationVehicleSzltListMeta = {},
    loadingRelationVehicleSzltList,
    loadingRelationCarrierSzltList,
  } = useSelector(getRegisterData);

  const action = isCarrierRelationType ? loadRelationCarrierSzltList : loadRelationVehicleSzltList;
  const currentList = isCarrierRelationType ? relationCarrierSzltList : relationVehicleSzltList;
  const currentMeta = isCarrierRelationType ? relationCarrierSzltListMeta : relationVehicleSzltListMeta;
  const loading = isCarrierRelationType ? loadingRelationCarrierSzltList : loadingRelationVehicleSzltList;

  useEffect(() => {
    if (isCarrierRelationType) {
      dispatch(loadRelationCarrierSzltListClear());
      return;
    }
    dispatch(loadRelationVehicleSzltListClear());
  }, []);

  const {
    getValues, control, watch,
  } = useForm({});

  const loadData = opts => {
    const filterData = getValues();

    const paginationOpts = getPaginationOpts(currentMeta, opts);

    const requestData = {
      ...paginationOpts,
      filter: {
        ...params,
        ...filterData,
      },
      sort,
    };
    const p1 = dispatch(action(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  useEffect(() => {
    const subscription = watch(values => {
      loadData({ page: 1 });
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    dispatch(action({ filter: { ...params } })).catch(responseLogWithNotification);
  }, []);

  const orderServicesColumns = [
    {
      dataField: 'order_service',
      text: 'Номер записи',
      sortField: 'registry_id',
      formatter: item => registryLinkFormatter(item?.number, REGISTRY_TYPE.ORDER_SERVICE, { registry_id: item?.id }),
    },
    {
      dataField: isCarrierRelationType ? 'contract_effective_date' : 'order_service_contract_effective_date',
      text: 'Дата начала срока действия договора',
      formatter: dateFormatter,
    },
    {
      dataField: isCarrierRelationType ? 'contract_expiration_date' : 'order_service_contract_expiration_date',
      text: 'Дата окончания срока действия договора',
      formatter: dateFormatter,
    },
    {
      dataField: 'order_service',
      text: 'Наименование СЗЛТ',
      sortField: 'order_service',
      formatter: item => getLegalEntityName(item),
    },
    {
      dataField: 'order_service',
      text: 'ОГРН СЗЛТ',
      sortField: 'ogrn',
      formatter: item => item?.ogrn,
    },
    {
      dataField: 'order_service',
      text: 'Номер договора с СЗЛТ',
      sortField: 'contract_number',
      formatter: (item, _) => _?.contract_number,
    },
    {
      dataField: 'vehicle',
      text: 'Статус связи',
      sortField: 'is_active',
      formatter: (_, item) => (item?.is_active ? 'Действующая' : 'Недействующая'),
    },
  ];

  const [sort, setSort] = useState(getDefaultSort(orderServicesColumns));

  const changeSort = fieldName => {
    if (sort && (sort === fieldName || sort === `-${fieldName}`)) {
      if (sort === `-${fieldName}`) {
        setSort(fieldName);
      } else if (sort === fieldName) {
        setSort('');
      }
    } else {
      setSort(`-${fieldName}`);
    }
  };

  useEffect(() => { loadData(); }, [sort]);

  return (
    <div className="afc-data">
      <div className="afc-wrapper">
        <div className="afc-header">
          <div className="afc-header-text">
            Связи с реестром СЗЛТ
          </div>
          <FieldDTO
            type="text"
            control={control}
            component={Switches}
            dtoObject={{
              name: 'only_active',
              type: String,
              validators: [],
              labelKey: 'registry.isAvailable',
            }}
            variant="material"
          />
          <div className="table-control-button">
            <div className="application-options ms-lg-auto">
              <TooltipWithPortal
                content={<Icons customIcon icon="more-vert" className="more-vert" />}
                classNamePortal="more-actions application-options-dropdown"
                tooltipContent={(
                  <div className="more-wrapper">
                    {isCarrierRelationType ? (
                      <Button
                        variant="link"
                        className="w-100 more-button"
                        onClick={() => setIsHistoryPopUp('szlt')}
                      >
                        История изменений
                      </Button>
                    ) : null}
                    <Button
                      variant="link"
                      className={classNames('w-100 more-button link-text', { disabled: !currentList?.length })}
                      onClick={currentList?.length && (() => setIsRegistryModal(true))}
                    >
                      Реестр СЗЛТ (из связей)
                    </Button>
                  </div>
                  )}
                classNameWrapper="more-tooltip-wrapper"
                className="more-tooltip"
              />
            </div>
          </div>
        </div>
        <div className="afc-body">
          <div className="grid-wrapper">
            <div className=" custom-scroll-theme">
              <Grid
                data={currentList}
                changeSort={changeSort}
                sort={sort}
                columns={orderServicesColumns}
                loading={loading}
              />
            </div>
            <Pagination
              meta={currentMeta}
              list={currentList}
              handleChangeMeta={loadData}
            />
          </div>
        </div>
      </div>
      {
        isRegistryModal ? (
          <RegistryPageModal
            type="szlt"
            customFilter={{
              numbers: (currentList || []).map(i => i?.order_service.number),
            }}
            onClose={() => setIsRegistryModal(false)}
          />
        ) : null
      }
    </div>
  );
}
